
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { SubmitHandler } from "react-hook-form";

interface FormValues {
  date: string;
  mood: string;
  energy_level?: string;
  sleep_quality?: string;
  sleep_duration?: string;
  physical_activity?: string;
  water_intake?: string;
  appetite?: string;
  stress_level?: string;
  symptoms: string;
  medication_adherence?: string;
  notes: string;
}

export const useJournalForm = (onOpenChange: (open: boolean) => void) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const form = useForm<FormValues>({
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      mood: "",
      energy_level: "",
      sleep_quality: "",
      sleep_duration: "",
      physical_activity: "",
      water_intake: "",
      appetite: "",
      stress_level: "",
      symptoms: "",
      medication_adherence: "",
      notes: "",
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Vous devez être connecté pour ajouter une entrée",
        });
        return;
      }

      const journalEntry = {
        date: values.date,
        mood: values.mood,
        symptoms: values.symptoms ? values.symptoms.split(',').map(s => s.trim()) : [],
        notes: values.notes || null,
        energy_level: values.energy_level || null,
        sleep_quality: values.sleep_quality || null,
        sleep_duration: values.sleep_duration || null,
        physical_activity: values.physical_activity || null,
        water_intake: values.water_intake || null,
        appetite: values.appetite || null,
        stress_level: values.stress_level || null,
        medication_adherence: values.medication_adherence || null,
        user_id: user.id,
      };

      // Vérifier s'il existe déjà une entrée pour cette date
      const { data: existingEntry } = await supabase
        .from("health_journal")
        .select("id")
        .eq("user_id", user.id)
        .eq("date", values.date)
        .maybeSingle();

      let error;
      
      if (existingEntry) {
        // Mettre à jour l'entrée existante
        const response = await supabase
          .from("health_journal")
          .update(journalEntry)
          .eq("id", existingEntry.id);
        
        error = response.error;
        
        if (!error) {
          toast({
            title: "Entrée mise à jour",
            description: "Votre entrée de journal a été mise à jour avec succès",
          });
        }
      } else {
        // Insérer une nouvelle entrée
        const response = await supabase
          .from("health_journal")
          .insert(journalEntry);
        
        error = response.error;
        
        if (!error) {
          toast({
            title: "Entrée ajoutée",
            description: "Votre entrée a été ajoutée avec succès",
          });
        }
      }

      if (error) throw error;
      
      queryClient.invalidateQueries({ queryKey: ["journal"] });
      onOpenChange(false);
      form.reset();
    } catch (error) {
      console.error("Error in onSubmit:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'ajouter l'entrée",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    form,
    isLoading,
    onSubmit,
  };
};
